import React, { useState, useEffect } from 'react';
import { Play, Pause, CheckCircle, Clock, FileText, AlertCircle, Zap, Target, Coffee, Trophy, ChevronDown, ChevronUp, Eye, Edit, Download, X, Filter, Settings, Copy, RefreshCw, GitBranch } from 'lucide-react';
import api from '../../services/api';
import PreviewModal from '../PreviewModal';
import SetPriceModal from '../SetPriceModal';
import EnquiryDetailsModal from './EnquiryDetailsModal';
import { useToast } from '../modern/ToastContainer';
import { itineraryService } from '../../services/itinerary';
import VersionsModal from '../modern/VersionsModal';
import { itineraryVersionService } from '../../services/itineraryVersionService';
import { formatLocalDateTime, formatLocalDate, formatLocalTime, formatDuration } from '../../utils/dateTimeUtils';
interface MyAssignment {
  id: number;
  enquiry_id: number;
  enquiry_request_id?: number;
  agency_name: string;
  customer_name?: string;
  customer_reference?: string;
  pic_name?: string;
  travel_date: string;
  plan_text: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assigned_at: string;
  started_at?: string;
  completed_at?: string;
  estimated_hours: number;
  actual_hours?: number;
  status: 'assigned' | 'in_progress' | 'completed' | 'paused';
  pause_reason?: string;
  paused_at?: string;
  completion_step?: number;
  expected_itinerary_count: number;
  created_itinerary_count: number;
  completed_itinerary_count: number;
  itineraries?: Array<{id: number, itinerary_option: string, status: string}>;
  is_rework?: boolean;
  rework_id?: number;
  rework_number?: number;
  changes_requested?: string;
  pax_changed?: boolean;
  new_adults?: number;
  new_kids?: number;
  date_changed?: boolean;
  new_travel_date?: string;
  selected_itinerary_id?: number;
  selected_itinerary_name?: string;
  attachment_file_path?: string;
  attachment_file_name?: string;
  number_of_adults?: number;
  number_of_kids?: number;
  number_of_nights?: number;
  quotation_number?: string;
  version_number?: number;
  base_quotation_number?: string;
  base_version_number?: number;
}

interface TimeLog {
  id: number;
  assignment_id: number;
  start_time: string;
  end_time?: string;
  duration_minutes: number;
  activity_type: string;
  notes: string;
}

const MyAssignments: React.FC = () => {
  const [assignments, setAssignments] = useState<MyAssignment[]>([]);
  const [activeTimer, setActiveTimer] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedAssignment, setSelectedAssignment] = useState<MyAssignment | null>(null);
  const [timeNotes, setTimeNotes] = useState('');
  const [todayStats, setTodayStats] = useState({ completed: 0, hoursWorked: 0, streak: 0 });
  const [completionModal, setCompletionModal] = useState<{assignment: MyAssignment, itineraries: any[]} | null>(null);
  const [fileUploadModal, setFileUploadModal] = useState<{assignment: MyAssignment, linkedItineraries: any[]} | null>(null);
  const [expandedAssignments, setExpandedAssignments] = useState<Set<number>>(new Set());
  const [assignmentItineraries, setAssignmentItineraries] = useState<{[key: number]: any[]}>({});
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    travelFromDate: '',
    travelToDate: '',
    enquiryId: '',
    agencyName: '',
    status: ''
  });
  const [allAssignments, setAllAssignments] = useState<MyAssignment[]>([]);
  const [viewItinerary, setViewItinerary] = useState<any | null>(null);
  const [setPriceItinerary, setSetPriceItinerary] = useState<any | null>(null);
  const [lastUpdated, setLastUpdated] = useState<number>(0);
  const [versionsItineraryId, setVersionsItineraryId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [viewPlanModal, setViewPlanModal] = useState<MyAssignment | null>(null);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [selectedItineraries, setSelectedItineraries] = useState<number[]>([]);
  const [itinerarySearchQuery, setItinerarySearchQuery] = useState('');
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const { showToast } = useToast();

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    fetchAssignments();
    return () => clearInterval(timer);
  }, []);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/my-assignments');
      if (response.data.success) {
        const assignmentsData = response.data.data;
        setAllAssignments(assignmentsData);
        setAssignments(assignmentsData);
        setLastUpdated(Date.now());
        
        // Calculate today's stats
        const today = new Date();
        const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
        
        const completedToday = assignmentsData.filter((a: MyAssignment) => {
          const isCompleted = a.status === 'completed';
          const hasCompletedAt = a.completed_at;
          
          if (!isCompleted || !hasCompletedAt) {
            return false;
          }
          
          const completedDate = new Date(hasCompletedAt);
          const isToday = completedDate >= todayStart && completedDate < todayEnd;
          
          return isToday;
        });
        
        const hoursWorkedToday = completedToday.reduce((sum: number, a: MyAssignment) => 
          sum + (a.actual_hours || 0), 0
        );
        
        setTodayStats({
          completed: completedToday.length,
          hoursWorked: hoursWorkedToday,
          streak: 3 // Mock streak data
        });
      }
    } catch (error) {
      console.error('Error fetching assignments:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (data: MyAssignment[]) => {
    let filtered = [...data];
    
    if (filters.fromDate) {
      filtered = filtered.filter(a => new Date(a.assigned_at) >= new Date(filters.fromDate));
    }
    if (filters.toDate) {
      filtered = filtered.filter(a => new Date(a.assigned_at) <= new Date(filters.toDate));
    }
    if (filters.enquiryId) {
      filtered = filtered.filter(a => a.enquiry_id.toString().includes(filters.enquiryId));
    }
    if (filters.agencyName) {
      filtered = filtered.filter(a => a.agency_name.toLowerCase().includes(filters.agencyName.toLowerCase()));
    }
    
    setAssignments(filtered);
  };

  const handleFilterChange = (key: string, value: string) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    // Apply filters with the new filter values
    let filtered = [...allAssignments];
    
    if (newFilters.fromDate) {
      filtered = filtered.filter(a => new Date(a.assigned_at) >= new Date(newFilters.fromDate));
    }
    if (newFilters.toDate) {
      filtered = filtered.filter(a => new Date(a.assigned_at) <= new Date(newFilters.toDate));
    }
    if (newFilters.travelFromDate) {
      filtered = filtered.filter(a => new Date(a.travel_date) >= new Date(newFilters.travelFromDate));
    }
    if (newFilters.travelToDate) {
      filtered = filtered.filter(a => new Date(a.travel_date) <= new Date(newFilters.travelToDate));
    }
    if (newFilters.enquiryId) {
      filtered = filtered.filter(a => a.enquiry_id.toString().includes(newFilters.enquiryId));
    }
    if (newFilters.agencyName) {
      filtered = filtered.filter(a => a.agency_name.toLowerCase().includes(newFilters.agencyName.toLowerCase()));
    }
    if (newFilters.status) {
      filtered = filtered.filter(a => a.status === newFilters.status);
    }
    
    setAssignments(filtered);
  };

  const clearFilters = () => {
    setFilters({ fromDate: '', toDate: '', travelFromDate: '', travelToDate: '', enquiryId: '', agencyName: '', status: '' });
    setAssignments(allAssignments);
  };

  const handleViewItinerary = async (itineraryId: number) => {
    try {
      const response = await api.get(`/itineraries/${itineraryId}`);
      if (response.data) {
        setViewItinerary(response.data);
      }
    } catch (error) {
      console.error('Error loading itinerary:', error);
    }
  };

  const handleEditItinerary = (itineraryId: number) => {
    window.open(`/balify?edit=${itineraryId}`, '_blank');
  };

  const handleCopyItinerary = async (itineraryId: number) => {
    window.open(`/balify?copy=${itineraryId}`, '_blank');
  };

  const handleSetPrice = async (itineraryId: number) => {
    try {
      const itin = await itineraryService.getItinerary(itineraryId);
      setSetPriceItinerary(itin);
    } catch (error) {
      console.error('Error loading itinerary for pricing:', error);
      showToast('Failed to load itinerary', 'error');
    }
  };

  const handleFileUpload = async () => {
    console.log('=== handleFileUpload START ===');
    console.log('fileUploadModal:', fileUploadModal);
    console.log('uploadFiles:', uploadFiles);
    
    if (!fileUploadModal || uploadFiles.length === 0) {
      console.log('No modal or no files, returning');
      return;
    }
    
    try {
      // Get selected itineraries from session storage
      const sessionKey = `assignment_${fileUploadModal.assignment.id}_selected_itineraries`;
      const savedSelection = sessionStorage.getItem(sessionKey);
      console.log('Session key for upload:', sessionKey);
      console.log('Saved selection for upload:', savedSelection);
      
      if (!savedSelection) {
        showToast('No itineraries selected. Please go back to step 1.', 'error');
        return;
      }
      
      // Single API call to complete assignment with itineraries and files
      const selectedIds = JSON.parse(savedSelection);
      const formData = new FormData();
      formData.append('selected_itinerary_ids', JSON.stringify(selectedIds));
      uploadFiles.forEach((file: File) => {
        formData.append('files', file);
      });
      
      console.log('Making API call to complete assignment');
      console.log('Selected IDs:', selectedIds);
      console.log('Files:', uploadFiles.map(f => f.name));
      
      await api.post(`/work-assignments/${fileUploadModal.assignment.id}/complete`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Clear session storage when assignment is fully completed
      sessionStorage.removeItem(sessionKey);
      
      showToast('Assignment completed successfully!', 'success');
      setFileUploadModal(null);
      setUploadFiles([]);
      fetchAssignments();
      console.log('=== handleFileUpload SUCCESS ===');
    } catch (error) {
      console.error('Error completing assignment:', error);
      showToast('Error completing assignment', 'error');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setUploadFiles(files);
  };

  const handleDownload = async (itineraryId: number, isAgent = false) => {
    try {
      const fullItinerary = await itineraryService.getItinerary(itineraryId);
      const givenPrice = fullItinerary?.given_price;
      let hasSetPrice = false;
      
      if (givenPrice && typeof givenPrice === 'object') {
        hasSetPrice = Object.values(givenPrice).some((priceData: any) => 
          priceData && typeof priceData === 'object' && priceData.total && priceData.total > 0
        );
      }
      
      if (!hasSetPrice) {
        showToast('Please set price before downloading', 'error');
        return;
      }
      
      await itineraryService.downloadItinerary(itineraryId, isAgent);
      showToast('Download started', 'success');
    } catch (error) {
      console.error('Error downloading:', error);
      showToast('Failed to download', 'error');
    }
  };

  const handleDownloadAgent = async (itineraryId: number) => {
    try {
      await itineraryService.downloadItinerary(itineraryId, true);
      showToast('Download started', 'success');
    } catch (error) {
      console.error('Error downloading agent itinerary:', error);
      showToast('Failed to download', 'error');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'assigned': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const calculateDuration = (startTime: string, endTime?: string) => {
    const start = new Date(startTime + 'Z'); // Force UTC parsing
    const end = endTime ? new Date(endTime + 'Z') : new Date();
    const diffMs = end.getTime() - start.getTime();
    
    // Ensure positive duration
    if (diffMs < 0) return '0m';
    
    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    if (hours === 0) {
      return `${minutes}m`;
    }
    return `${hours}h ${minutes}m`;
  };

  const handleContinueRework = async (assignment: MyAssignment) => {
    if (!assignment.selected_itinerary_id) {
      window.location.href = '/balify';
      return;
    }
    try {
      // Check if an itinerary already exists for this rework request
      const response = await api.get(`/itineraries/enquiry/${assignment.enquiry_id}`);
      if (response.data.success) {
        const existing = response.data.data.find(
          (itin: any) => itin.enquiry_request_id === assignment.enquiry_request_id
        );
        if (existing) {
          // Already created — just open it
          showToast(`Opening existing WH_${existing.id}`, 'success');
          window.location.href = `/balify?edit=${existing.id}`;
          return;
        }
      }
      // No itinerary yet — create a new version
      const res = await itineraryVersionService.createVersion(assignment.selected_itinerary_id);
      if (res.data.success) {
        showToast(`Version ${res.data.version_number} created: ${res.data.new_wyn_itinerary_id || 'WH_' + res.data.new_itinerary_id} under ${res.data.quotation_number}`, 'success');
        window.location.href = `/balify?edit=${res.data.new_itinerary_id}`;
      } else {
        window.location.href = '/balify';
      }
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Failed', 'error');
      window.location.href = '/balify';
    }
  };

  const handleStartWork = async (assignmentId: number, assignment?: MyAssignment) => {
    try {
      await api.post(`/work-assignments/${assignmentId}/start`);
      if (assignment?.is_rework && assignment.selected_itinerary_id) {
        setVersionsItineraryId(assignment.selected_itinerary_id);
      } else {
        window.location.href = '/balify?create=true';
      }
    } catch (error) {
      console.error('Error starting work:', error);
    }
  };

  const handlePauseWork = async (assignmentId: number) => {
    try {
      await api.post(`/work-assignments/${assignmentId}/pause`);
      setActiveTimer(null);
      fetchAssignments();
    } catch (error) {
      console.error('Error pausing work:', error);
    }
  };

  const handleCompleteWork = async (assignment: MyAssignment) => {
    try {
      console.log('=== handleCompleteWork START ===');
      console.log('Assignment:', assignment);
      console.log('Completion step:', assignment.completion_step);
      
      // Check completion step to determine which modal to show
      if (assignment.completion_step === 1) {
        console.log('Step 2: Showing file upload modal');
        // Step 2: Show file upload modal
        const response = await api.get(`/itineraries/enquiry/${assignment.enquiry_id}`);
        if (response.data.success) {
          const linkedItineraries = response.data.data.filter((itin: any) => 
            itin.assignment_id === assignment.id
          );
          console.log('Linked itineraries for step 2:', linkedItineraries);
          setFileUploadModal({ assignment, linkedItineraries });
        }
        return;
      }
      
      console.log('Step 1: Getting unassigned itineraries');
      // Step 1: Get unassigned itineraries for this customer
      const response = await api.get(`/itineraries/customer/${assignment.enquiry_id}/unassigned`);
      console.log('Unassigned itineraries response:', response.data);
      
      if (response.data && response.data.success) {
        const allItineraries = response.data.data || [];
        
        // Load previously selected itineraries from session storage
        const sessionKey = `assignment_${assignment.id}_selected_itineraries`;
        console.log('Session key:', sessionKey);
        const savedSelection = sessionStorage.getItem(sessionKey);
        console.log('Saved selection from session:', savedSelection);
        
        if (savedSelection) {
          try {
            const savedIds = JSON.parse(savedSelection);
            console.log('Parsed saved IDs:', savedIds);
            console.log('Type of saved IDs:', typeof savedIds, Array.isArray(savedIds));
            console.log('Setting selectedItineraries state to saved IDs');
            setSelectedItineraries(savedIds);
            console.log('selectedItineraries state should now be:', savedIds);
            
            // Verify the state was set by checking after a brief delay
            setTimeout(() => {
              console.log('selectedItineraries state after setTimeout:', selectedItineraries);
            }, 100);
          } catch (e) {
            console.error('Error parsing saved selection:', e);
          }
        } else {
          console.log('No saved selection found, clearing selectedItineraries');
          setSelectedItineraries([]);
        }
        
        console.log('Setting modal with itineraries:', allItineraries);
        console.log('Current selectedItineraries before setting modal:', selectedItineraries);
        setCompletionModal({ assignment, itineraries: allItineraries });
        console.log('Completion modal set, selectedItineraries should be:', selectedItineraries);
      } else {
        console.log('No itineraries found or unsuccessful response');
        setCompletionModal({ assignment, itineraries: [] });
      }
      console.log('=== handleCompleteWork END ===');
    } catch (error) {
      console.error('Error fetching itineraries:', error);
      setCompletionModal({ assignment, itineraries: [] });
    }
  };

  const handleConfirmCompletion = () => {
    console.log('=== handleConfirmCompletion START ===');
    if (!completionModal) {
      console.log('No completion modal, returning');
      return;
    }
    
    console.log('Completion modal assignment:', completionModal.assignment);
    console.log('Selected itineraries:', selectedItineraries);
    
    // Save selected itineraries to session storage
    const sessionKey = `assignment_${completionModal.assignment.id}_selected_itineraries`;
    console.log('Saving to session key:', sessionKey);
    console.log('Saving data:', JSON.stringify(selectedItineraries));
    sessionStorage.setItem(sessionKey, JSON.stringify(selectedItineraries));
    
    if (completionModal.assignment.status === 'completed') {
      console.log('Assignment is completed, calling backend to edit');
      // Edit existing assignment - call backend
      const editAssignment = async () => {
        try {
          const endpoint = `/work-assignments/${completionModal.assignment.id}/edit-itineraries`;
          const formData = new FormData();
          formData.append('selected_itinerary_ids', JSON.stringify(selectedItineraries));
          
          await api.post(endpoint, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          
          // Clear session storage and close modal
          sessionStorage.removeItem(sessionKey);
          setActiveTimer(null);
          setCompletionModal(null);
          setSelectedItineraries([]);
          setItinerarySearchQuery('');
          fetchAssignments();
        } catch (error) {
          console.error('Error updating assignment:', error);
          showToast('Error completing assignment', 'error');
        }
      };
      editAssignment();
    } else {
      console.log('Assignment not completed, proceeding to step 2');
      // Step 1 completion - session only, no backend call
      showToast('Step 1 completed. Please upload files for each itinerary.', 'success');
      setCompletionModal(null);
      setSelectedItineraries([]);
      setItinerarySearchQuery('');
      setAttachmentFile(null);
      
      // Show step 2 modal with selected itineraries from session
      const updatedAssignment = { ...completionModal.assignment, completion_step: 1 };
      const linkedItineraries = selectedItineraries.map((id: number) => 
        completionModal.itineraries.find((itin: any) => itin.id === id)
      ).filter(Boolean);
      console.log('Updated assignment for step 2:', updatedAssignment);
      console.log('Linked itineraries for step 2:', linkedItineraries);
      setFileUploadModal({ assignment: updatedAssignment, linkedItineraries });
    }
    console.log('=== handleConfirmCompletion END ===');
  };

  const toggleItineraries = async (assignmentId: number, enquiryId: number) => {
    const newExpanded = new Set(expandedAssignments);
    
    if (expandedAssignments.has(assignmentId)) {
      newExpanded.delete(assignmentId);
    } else {
      newExpanded.add(assignmentId);
      // Fetch itineraries if not already loaded
      if (!assignmentItineraries[assignmentId]) {
        try {
          const response = await api.get(`/itineraries/enquiry/${enquiryId}`);
          if (response.data.success) {
            // Filter itineraries by enquiry_request_id
            const assignment = assignments.find(a => a.id === assignmentId);
            const filteredItineraries = assignment?.enquiry_request_id 
              ? response.data.data.filter((itin: any) => itin.enquiry_request_id === assignment.enquiry_request_id)
              : response.data.data;
            
            setAssignmentItineraries(prev => ({
              ...prev,
              [assignmentId]: filteredItineraries
            }));
          }
        } catch (error) {
          console.error('Error fetching itineraries:', error);
        }
      }
    }
    
    setExpandedAssignments(newExpanded);
  };

  const isOverdue = (assignment: MyAssignment) => {
    if (!assignment.started_at) return false;
    const startTime = new Date(assignment.started_at);
    const estimatedEndTime = new Date(startTime.getTime() + (assignment.estimated_hours * 60 * 60 * 1000));
    return currentTime > estimatedEndTime && assignment.status !== 'completed';
  };

  const getTimeAgo = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} min ago`;
    return `${Math.floor(minutes / 60)}h ago`;
  };

  const handleRefresh = () => {
    fetchAssignments();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-3 sm:p-6">
      <div className="max-w-full mx-auto">
        {/* Gamified Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 space-y-2 sm:space-y-0">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">My Work Dashboard</h1>
              <p className="text-gray-600">Keep the momentum going! 🚀</p>
            </div>
            <div className="text-left sm:text-right">
              <div className="text-xl sm:text-2xl font-bold text-indigo-600">{todayStats.streak} day streak! 🔥</div>
              <div className="text-sm text-gray-600">You're on fire!</div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">My Assignments</h2>
            <div className="flex items-center space-x-4">
              {lastUpdated > 0 && (
                <div className="flex items-center text-sm text-gray-500">
                  <Clock className="h-4 w-4 mr-1" />
                  Updated {getTimeAgo(lastUpdated)}
                </div>
              )}
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium shadow-sm"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>
          
          <div className="bg-white p-3 sm:p-4 rounded-xl shadow-lg border border-gray-200 mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Assigned From</label>
                <input
                  type="date"
                  value={filters.fromDate}
                  onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Assigned To</label>
                <input
                  type="date"
                  value={filters.toDate}
                  onChange={(e) => handleFilterChange('toDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Travel From</label>
                <input
                  type="date"
                  value={filters.travelFromDate}
                  onChange={(e) => handleFilterChange('travelFromDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Travel To</label>
                <input
                  type="date"
                  value={filters.travelToDate}
                  onChange={(e) => handleFilterChange('travelToDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Enquiry ID</label>
                <input
                  type="text"
                  placeholder="Search by enquiry ID"
                  value={filters.enquiryId}
                  onChange={(e) => handleFilterChange('enquiryId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Agency Name</label>
                <input
                  type="text"
                  placeholder="Search by agency"
                  value={filters.agencyName}
                  onChange={(e) => handleFilterChange('agencyName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Statuses</option>
                  <option value="assigned">Assigned</option>
                  <option value="in_progress">In Progress</option>
                  <option value="paused">Paused</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>
            <div className="mt-4 flex justify-center sm:justify-end">
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
              >
                Clear All Filters
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {assignments.map((assignment) => (
            <div 
              key={assignment.id} 
              className={`bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border-l-4 p-4 ${
                assignment.status === 'completed' ? 'border-l-green-500' :
                assignment.status === 'in_progress' ? 'border-l-blue-500' : 
                assignment.status === 'paused' ? 'border-l-yellow-500' : 'border-l-purple-500'
              }`}
            >
              {/* Header Row */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Quotation badge - clickable */}
                  {assignment.quotation_number ? (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-800 text-xs font-mono font-bold rounded-lg cursor-pointer hover:bg-blue-200"
                      onClick={() => { const id = (assignment as any).first_itinerary_id || null; if (id) setVersionsItineraryId(id); }}
                      title="View all versions"
                    >
                      <GitBranch className="w-3 h-3" />
                      {assignment.quotation_number}/{assignment.version_number}
                    </span>
                  ) : (
                    <span className="font-mono text-xs font-semibold text-gray-500">WN_{assignment.enquiry_id}</span>
                  )}
                  {/* Enquiry request ID - separate */}
                  <span className="text-xs text-gray-400 font-mono">ER_{assignment.enquiry_request_id}</span>
                  {assignment.is_rework && (
                    <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded">
                      REWORK{assignment.rework_number ? ` #${assignment.rework_number}` : ''}
                    </span>
                  )}
                  {isOverdue(assignment) && (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded bg-red-100 text-red-800">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      Overdue
                    </span>
                  )}
                </div>
                <span className={`px-2 py-1 text-xs font-semibold rounded ${getStatusColor(assignment.status)}`}>
                  {assignment.status === 'paused' ? (
                    <div className="flex flex-col">
                      <span>Paused</span>
                      {assignment.pause_reason && (
                        <span className="text-xs opacity-75 mt-0.5">{assignment.pause_reason}</span>
                      )}
                    </div>
                  ) : (
                    assignment.status.replace('_', ' ')
                  )}
                </span>
              </div>

              {/* Agency Name */}
              <h3 className="text-lg font-bold text-gray-900 mb-3">{assignment.agency_name}</h3>

              {/* Key Info - Single Line */}
              <div className="flex items-center gap-3 text-sm text-gray-600 mb-3">
                <span>📅 {formatLocalDate(assignment.travel_date)}</span>
                <span>•</span>
                <span>👥 {assignment.number_of_adults}A{(assignment.number_of_kids || 0) > 0 ? ` + ${assignment.number_of_kids}K` : ''}</span>
                {(assignment.number_of_nights || 0) > 0 && (
                  <>
                    <span>•</span>
                    <span>🌙 {assignment.number_of_nights}N</span>
                  </>
                )}
              </div>

              {/* Base Itinerary (Reworks Only) */}
              {assignment.is_rework && assignment.selected_itinerary_id && (
                <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded">
                  <span className="text-xs text-green-700 font-medium">🎯 Rework on: {assignment.base_quotation_number ? `${assignment.base_quotation_number}/${assignment.base_version_number}` : `WH_${assignment.selected_itinerary_id}`}</span>
                </div>
              )}

              {/* Timeline */}
              <div className="text-sm text-gray-500 mb-4">
                {assignment.status === 'in_progress' && assignment.started_at
                  ? `Started ${formatLocalDateTime(assignment.started_at)}`
                  : assignment.status === 'completed' && assignment.completed_at
                  ? `Completed ${formatLocalDateTime(assignment.completed_at)}`
                  : `Assigned ${formatLocalDateTime(assignment.assigned_at)}`
                }
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                {assignment.status === 'assigned' && (
                  <button
                    onClick={() => handleStartWork(assignment.id, assignment)}
                    className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                  >
                    <Zap className="h-4 w-4 mr-2" />
                    {assignment.is_rework ? '🔀 Create Version & Start' : 'Start Working'}
                  </button>
                )}
                
                {assignment.status === 'in_progress' && (
                  <>
                    <button
                      onClick={() => {
                        if (assignment.is_rework && assignment.selected_itinerary_id) {
                          setVersionsItineraryId(assignment.selected_itinerary_id);
                        } else {
                          window.location.href = '/balify?create=true';
                        }
                      }}
                      className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      {assignment.is_rework ? '🔀 Create Version' : 'Continue Working'}
                    </button>
                    <button
                      onClick={() => {
                        console.log('COMPLETE BUTTON CLICKED!');
                        handleCompleteWork(assignment);
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                    >
                      {assignment.completion_step === 1 ? 'Upload Files (Step 2)' : 'Complete (Step 1)'}
                    </button>
                    <button
                      onClick={() => setViewPlanModal(assignment)}
                      className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  </>
                )}
                
                {assignment.status === 'completed' && (
                  <>
                    <button
                      onClick={() => toggleItineraries(assignment.id, assignment.enquiry_id)}
                      className="flex-1 flex items-center justify-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      {expandedAssignments.has(assignment.id) ? 'Hide' : 'View'} Itineraries
                    </button>
                    <button
                      onClick={() => setViewPlanModal(assignment)}
                      className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                      title="View Plan"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleCompleteWork(assignment)}
                      className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                      title="Edit Itineraries"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>

              
              {/* Expandable Itineraries Section */}
              {expandedAssignments.has(assignment.id) && (
                <div className="mt-4 border-t pt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Itineraries for this Request</h4>
                  {assignmentItineraries[assignment.id] ? (
                    assignmentItineraries[assignment.id].length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {assignmentItineraries[assignment.id].map((itinerary) => (
                          <div key={itinerary.id} className="bg-gray-50 rounded-lg p-4 border">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <div className="flex items-center mb-1">
                                  <span className="bg-emerald-100 text-emerald-800 px-2 py-1 rounded text-xs font-mono mr-2">{itinerary.quotation_number ? `${itinerary.quotation_number}/${itinerary.version_number}` : `WH_${itinerary.id}`}</span>
                                  <h5 className="font-medium text-gray-900 text-sm">{itinerary.itinerary_name}</h5>
                                </div>
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  itinerary.status === 'completed' ? 'bg-green-100 text-green-800' :
                                  itinerary.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {itinerary.status}
                                </span>
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 mb-3">
                              Created: {formatLocalDate(itinerary.created_at)}
                            </p>
                            <div className="flex space-x-2">
                              <button 
                                onClick={() => handleViewItinerary(itinerary.id)}
                                className="flex items-center px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200"
                                title="Preview"
                              >
                                👁️
                              </button>
                              <button 
                                onClick={() => handleEditItinerary(itinerary.id)}
                                className="flex items-center px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs hover:bg-gray-200"
                                title="Edit Itinerary"
                              >
                                <Settings className="h-3 w-3" />
                              </button>
                              <button 
                                onClick={() => handleCopyItinerary(itinerary.id)}
                                className="flex items-center px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs hover:bg-purple-200"
                                title="Copy"
                              >
                                📋
                              </button>
                              <button 
                                onClick={() => handleSetPrice(itinerary.id)}
                                className="flex items-center px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs hover:bg-orange-200"
                                title="Set Price"
                              >
                                💰
                              </button>
                              <button 
                                onClick={() => handleDownload(itinerary.id)}
                                className="flex items-center px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs hover:bg-indigo-200"
                                title="Download"
                              >
                                📥
                              </button>
                              <button 
                                onClick={() => handleDownload(itinerary.id, true)}
                                className="flex items-center px-2 py-1 bg-green-100 text-green-700 rounded text-xs hover:bg-green-200"
                                title="Agent Download"
                              >
                                📄
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-gray-500">
                        <FileText className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p>No itineraries created yet for this enquiry.</p>
                      </div>
                    )
                  ) : (
                    <div className="text-center py-6">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="text-gray-500 mt-2">Loading itineraries...</p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Timer Section */}
              {activeTimer === assignment.id && (
                <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-bold text-blue-900 flex items-center">
                      <Zap className="h-4 w-4 mr-1" />
                      🔥 Working Hard!
                    </span>
                    <span className="text-xl font-mono text-blue-900 bg-white px-3 py-1 rounded-lg shadow">
                      {assignment.started_at && calculateDuration(assignment.started_at)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <input
                      type="text"
                      placeholder="What are you working on right now? 📝"
                      value={timeNotes}
                      onChange={(e) => setTimeNotes(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      onClick={() => {
                        // Save time log with notes
                        setTimeNotes('');
                      }}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 font-medium"
                    >
                      Log Activity
                    </button>
                  </div>
                </div>
              )}
            </div>
        ))}
        </div>
        
        {assignments.length === 0 && (
          <div className="text-center py-16 bg-white rounded-xl shadow-lg">
            <Coffee className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">All caught up! 🎉</h3>
            <p className="text-gray-500">No assignments right now. Time for a coffee break! ☕</p>
          </div>
        )}
      </div>
      
      {/* Completion Modal */}
      {completionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto relative">
            <button
              onClick={() => {
                setCompletionModal(null);
                setSelectedItineraries([]);
                setItinerarySearchQuery('');
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {completionModal.assignment.status === 'completed' ? 'Edit Assignment Itineraries' : 'Step 1: Select Itineraries'}
              </h2>
              <p className="text-gray-600 mb-4">
                {completionModal.assignment.status === 'completed' 
                  ? <>Update the itineraries linked to <strong>{completionModal.assignment.agency_name}</strong>:</>
                  : <>Select the itineraries you created for <strong>{completionModal.assignment.agency_name}</strong>:</>
                }
              </p>
              
              {/* Search Filter */}
              <div className="mb-4">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Search by ID or name..."
                    value={itinerarySearchQuery}
                    onChange={(e) => {
                      const query = e.target.value;
                      setItinerarySearchQuery(query);
                      if (query.trim()) {
                        const filtered = completionModal.itineraries.filter((itin: any) => 
                          itin.id.toString().includes(query) ||
                          itin.itinerary_name.toLowerCase().includes(query.toLowerCase())
                        );
                        setCompletionModal(prev => prev ? {...prev, itineraries: filtered} : null);
                      } else {
                        setCompletionModal(prev => prev ? {...prev, itineraries: prev.itineraries} : null);
                      }
                    }}
                    onKeyPress={(e) => e.key === 'Enter' && console.log('Search triggered')}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    onClick={() => console.log('Search clicked')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                  >
                    Search
                  </button>
                </div>
              </div>
              
              <div className="space-y-3 mb-6">
                {completionModal.itineraries.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p className="mb-2">No unassigned itineraries found for {completionModal.assignment.agency_name}.</p>
                    <p className="text-sm">Create itineraries first, then complete the assignment.</p>
                  </div>
                ) : (
                  completionModal.itineraries.map((itinerary) => {
                    console.log('Rendering itinerary checkbox for ID:', itinerary.id, 'checked:', selectedItineraries.includes(itinerary.id));
                    return (
                      <label key={itinerary.id} className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input 
                          type="checkbox" 
                          className="mr-3 h-4 w-4 text-blue-600"
                          checked={selectedItineraries.includes(itinerary.id)}
                          onChange={(e) => {
                            console.log('=== ITINERARY CHECKBOX CHANGE ===');
                            console.log('Itinerary ID:', itinerary.id);
                            console.log('Checked:', e.target.checked);
                            console.log('Current selectedItineraries:', selectedItineraries);
                            
                            if (e.target.checked) {
                              const newSelection = [...selectedItineraries, itinerary.id];
                              console.log('Adding itinerary, new selection:', newSelection);
                              setSelectedItineraries(newSelection);
                            } else {
                              const newSelection = selectedItineraries.filter((id: number) => id !== itinerary.id);
                              console.log('Removing itinerary, new selection:', newSelection);
                              setSelectedItineraries(newSelection);
                            }
                          }}
                        />
                        <div className="flex-1">
                          <div className="flex items-center mb-1">
                            <span className="bg-emerald-600 text-white px-3 py-1.5 rounded text-base font-bold font-mono mr-3">{itinerary.quotation_number ? `${itinerary.quotation_number}/${itinerary.version_number}` : `WH_${itinerary.id}`}</span>
                            <div className="font-medium text-gray-900">{itinerary.itinerary_name}</div>
                          </div>
                          <div className="text-sm text-gray-500">
                            Status: {itinerary.status} • Updated: {new Date(itinerary.updated_at || itinerary.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '/')}
                          </div>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setCompletionModal(null);
                    setSelectedItineraries([]);
                    setItinerarySearchQuery('');
                  }}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    console.log('🔥 CONTINUE TO STEP 2 BUTTON CLICKED');
                    console.log('Selected IDs at button click:', selectedItineraries);
                    console.log('Selected itinerary IDs:', selectedItineraries);
                    handleConfirmCompletion();
                  }}
                  className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700"
                >
                  {completionModal.assignment.status === 'completed' ? 
                    `Update Assignment (${selectedItineraries.length} selected)` :
                    completionModal.itineraries.length === 0 ? 
                      'Continue to Step 2' : 
                      `Continue to Step 2 (${selectedItineraries.length} selected)`
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Set Price Modal */}
      {setPriceItinerary && (
        <SetPriceModal
          itinerary={setPriceItinerary}
          onClose={() => setSetPriceItinerary(null)}
          onSuccess={() => {
            setSetPriceItinerary(null);
            showToast('Price updated successfully', 'success');
          }}
        />
      )}
      
      {/* View Itinerary Modal */}
      {viewItinerary && (
        <PreviewModal
          data={viewItinerary}
          onClose={() => setViewItinerary(null)}
        />
      )}
      
      {/* File Upload Modal (Step 2) */}
      {fileUploadModal && (() => {
        console.log('📁 STEP 2 PAGE RENDERED');
        console.log('File upload modal assignment:', fileUploadModal.assignment);
        console.log('Linked itineraries in step 2:', fileUploadModal.linkedItineraries);
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto relative">
              <button
                onClick={() => {
                  setFileUploadModal(null);
                  setUploadFiles([]);
                }}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
              >
                <X className="w-6 h-6" />
              </button>
              <div className="p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  ## Step 2: Upload Files for Itineraries
                </h2>
                <p className="text-gray-600 mb-4">
                  Upload DOCX/PDF files for each itinerary. Filename must contain the itinerary ID.
                </p>
                
                {/* Linked Itineraries */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Linked Itineraries:</h3>
                  <div className="space-y-4">
                    {fileUploadModal.linkedItineraries.map((itin) => (
                      <div key={itin.id} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center">
                            <span className="bg-emerald-100 text-emerald-800 px-2 py-1 rounded text-xs font-mono mr-2">{itin.quotation_number ? `${itin.quotation_number}/${itin.version_number}` : `WH_${itin.id}`}</span>
                            <span className="text-sm font-medium">{itin.itinerary_name}</span>
                          </div>
                          <button
                            onClick={() => handleDownload(itin.id)}
                            className="flex items-center px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs hover:bg-indigo-200"
                            title="Download DOCX"
                          >
                            📥 Download
                          </button>
                        </div>
                        
                        {/* Individual file upload for this itinerary */}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Upload file for this itinerary (DOCX/PDF)
                          </label>
                          <input
                            type="file"
                            accept=".docx,.pdf"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                // Update the uploadFiles array to include this file
                                const newFiles = uploadFiles.filter((f: File) => !f.name.includes(itin.id.toString()));
                                newFiles.push(file);
                                setUploadFiles(newFiles);
                              }
                            }}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Filename must follow format: <span className="font-mono">{itin.quotation_number || `WNH_XXXXX`}_vN_{itin.id}_Guest.docx</span> (older <span className="font-mono">WH_{itin.id}.docx</span> format also accepted)
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Summary of selected files */}
                {uploadFiles.length > 0 && (
                  <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">Files ready for upload:</h4>
                    <ul className="text-sm text-blue-700">
                      {uploadFiles.map((file: File, index: number) => (
                        <li key={index} className="flex items-center">
                          <span className="mr-2">📄</span>
                          {file.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      console.log('1) ⬅️ BACK TO STEP 1 BUTTON CLICKED');
                      const sessionKey = `assignment_${fileUploadModal.assignment.id}_selected_itineraries`;
                      const savedSelection = sessionStorage.getItem(sessionKey);
                      console.log('Back to Step 1 - Selected IDs from session:', savedSelection);
                      alert(`Back to Step 1 - Selected IDs: ${savedSelection}`);
                      
                      setFileUploadModal(null);
                      setUploadFiles([]);
                      
                      const assignmentForStep1 = { ...fileUploadModal.assignment };
                      delete assignmentForStep1.completion_step;
                      handleCompleteWork(assignmentForStep1);
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Back to Step 1
                  </button>
                  <button
                    onClick={() => {
                      console.log('2) ⬅️ BACK TO STEP 1 BUTTON CLICKED');
                      setFileUploadModal(null);
                      setUploadFiles([]);
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleFileUpload}
                    disabled={uploadFiles.length === 0}
                    className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Complete Assignment ({uploadFiles.length} files)
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      
      {versionsItineraryId && (
        <VersionsModal
          itineraryId={versionsItineraryId}
          selectedItineraryId={versionsItineraryId}
          onClose={() => setVersionsItineraryId(null)}
        />
      )}

      {/* View Plan Modal */}
      {viewPlanModal && (
        <EnquiryDetailsModal
          enquiry={viewPlanModal}
          onClose={() => setViewPlanModal(null)}
        />
      )}
    </div>
  );
};

export default MyAssignments;